// ─── State Registry ───────────────────────────────────────────────────────────
//
// Holds the active Phase 3 StateSchemaMap for runtime introspection.
// Provides lookup by schema key, by scope, and by page/component owner.
//
// This is the read-only view of the schema - mutations go through appStateStore.
// The registry is populated once at app boot (initStateFromSchema) and cleared
// on teardown.

import type { StateSchemaMap, StateDefinition, DerivedStateDefinition } from '@/core/schema/stateSchema';

class StateRegistry {
  private _projectId: string | null = null;
  private _map: StateSchemaMap = { definitions: [], derived: [] };

  /** Set by boot sequence. */
  register(projectId: string, map: StateSchemaMap): void {
    this._projectId = projectId;
    this._map = map;
  }

  get projectId(): string | null {
    return this._projectId;
  }

  /** Look up a state definition by its runtime key. */
  getDefinition(key: string): StateDefinition | undefined {
    return this._map.definitions.find((d) => d.key === key);
  }

  /** Look up a derived definition by its key. */
  getDerived(key: string): DerivedStateDefinition | undefined {
    return this._map.derived.find((d) => d.key === key);
  }

  /** All definitions for a given scope. */
  getByScope(scope: StateDefinition['scope']): StateDefinition[] {
    return this._map.definitions.filter((d) => d.scope === scope);
  }

  /** All definitions owned by a specific page or component. */
  getByOwner(pageId?: string, componentId?: string): StateDefinition[] {
    return this._map.definitions.filter((d) => {
      if (d.scope === 'page') return d.pageId === pageId;
      if (d.scope === 'component') return d.componentId === componentId;
      return false;
    });
  }

  /** All definition keys. */
  allKeys(): string[] {
    return this._map.definitions.map((d) => d.key);
  }

  clear(): void {
    this._projectId = null;
    this._map = { definitions: [], derived: [] };
  }
}

export const stateRegistry = new StateRegistry();
export type { StateDefinition, DerivedStateDefinition, StateSchemaMap };
